import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../utils/api';
import LoadingSpinner from '../components/LoadingSpinner';

export default function VerifyEmail() {
  const { token } = useParams<{ token: string }>();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        setStatus('error');
        setMessage('Invalid verification link');
        return;
      }

      try {
        const response = await api.get(`/auth/verify-email/${token}`);
        setStatus('success');
        setMessage(response.data.message);
        setEmail(response.data.email);
      } catch (err: unknown) {
        setStatus('error');
        const error = err as { response?: { data?: { error?: string } } };
        setMessage(error.response?.data?.error || 'Verification failed. The link may have expired.');
      }
    };

    verifyEmail();
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#0a0a18] via-[#0d0d24] to-[#0a0a18] px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img
            src="/images/dartsScheduler-trn.png"
            alt="Darts Scheduler"
            className="h-20 mx-auto mb-4"
          />
        </div>

        <div className="bg-[#1a1a3a] border border-[#2a2a4a] rounded-xl p-8 text-center">
          {status === 'loading' && (
            <>
              <LoadingSpinner size="lg" />
              <p className="text-gray-400 mt-4">Verifying your email...</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-900/30 flex items-center justify-center">
                <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">Email Verified</h1>
              <p className="text-gray-400 mb-2">{message}</p>
              {email && <p className="text-gray-500 text-sm mb-6">{email}</p>}
              <Link to="/login" className="btn-primary inline-block">
                Continue to Login
              </Link>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-900/30 flex items-center justify-center">
                <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">Verification Failed</h1>
              <p className="text-gray-400 mb-6">{message}</p>
              <div className="space-y-3">
                <Link to="/login" className="btn-primary inline-block w-full">
                  Go to Login
                </Link>
                <p className="text-gray-500 text-sm">
                  Need a new verification link?{' '}
                  <Link to="/resend-verification" className="text-[#1111d4] hover:underline">
                    Resend verification email
                  </Link>
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
